import {
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import Link from "@material-ui/core/Link";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SortableTable from "../../../../Components/SortableTable";
import Title from "../../../../Components/Title";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getToken,
  requestErrorHandler,
  tableAlternateColors,
} from "../../../../Utils/Common";
import EnvSettings from "../../../../Utils/EnvSettings";
import { useStyles } from "../../../../Utils/Styles";
import FeatureItemDialog from "./FeatureItemDialog";
import { showPopupSnackbar } from "../../../../redux/actions/snackbarActions";

const server = EnvSettings.server;

const transformFeatureItems = (records = []) =>
  records.map((feature) => {
    const listNamesMap = new Map();
    (feature?.ManufacturingFeatureLists || []).forEach((entry) => {
      const listName = entry?.ManufacturingFeatureListName;
      const listId = listName?.id ?? entry?.ManufacturingFeatureListNameId;
      const listLabel = (listName?.name || "").trim();
      if (!listLabel) {
        return;
      }
      if (!listNamesMap.has(listLabel)) {
        listNamesMap.set(listLabel, {
          id: listId ?? listLabel,
          name: listLabel,
        });
      }
    });
    return {
      id: feature?.id ?? null,
      name: feature?.name || "",
      description: feature?.description || "",
      StockItem: feature?.StockItem || null,
      updatedAt: feature?.updatedAt || null,
      listNames: Array.from(listNamesMap.values()),
    };
  });

export default function FeatureItems() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [features, setFeatures] = useState([]);
  const [filteredFeatures, setFilteredFeatures] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchFeatureItems = useCallback(() => {
    setIsLoading(true);
    setErrorMessage("");
    fetch(server + "/get_manufacturing_features", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          setFeatures([]);
          setErrorMessage(
            result.message ||
              t(
                "manufacturing.featureItemsFetchFailed",
                "Failed to fetch feature items."
              )
          );
          return;
        }
        const entries = Array.isArray(result.manufacturingFeatures)
          ? result.manufacturingFeatures
          : [];
        setFeatures(transformFeatureItems(entries));
      })
      .catch(() => {
        setFeatures([]);
        setErrorMessage(
          t(
            "manufacturing.featureItemsFetchFailed",
            "Failed to fetch feature items."
          )
        );
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, t]);

  useEffect(() => {
    fetchFeatureItems();
  }, [fetchFeatureItems]);

  useEffect(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      setFilteredFeatures(features);
      return;
    }
    const filtered = features.filter((feature) => {
      const fields = [
        feature.name,
        feature.description,
        feature.StockItem?.name,
        feature.StockItem?.sku,
        ...(feature.listNames || []).map((entry) => entry.name),
      ];
      return fields.some((value) =>
        value ? value.toLowerCase().includes(term) : false
      );
    });
    setFilteredFeatures(filtered);
  }, [features, searchValue]);

  const handleAddClick = () => {
    setActiveFeature(null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (isSubmitting) {
      return;
    }
    setIsDialogOpen(false);
    setActiveFeature(null);
  };

  const handleCreateFeature = (payload) => {
    setIsSubmitting(true);
    fetch(server + "/add_manufacturing_feature", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        feature: payload,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          dispatch(
            showPopupSnackbar({
              error: true,
              message:
                result.message ||
                t(
                  "manufacturing.featureCreateFailed",
                  "Failed to create feature."
                ),
            })
          );
          return;
        }
        dispatch(
          showPopupSnackbar({
            error: false,
            message: t(
              "manufacturing.featureCreated",
              "Feature created successfully."
            ),
          })
        );
        setIsDialogOpen(false);
        setActiveFeature(null);
        fetchFeatureItems();
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            error: true,
            message: t(
              "manufacturing.featureCreateFailed",
              "Failed to create feature."
            ),
          })
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleUpdateFeature = (payload) => {
    if (!activeFeature) {
      return;
    }
    setIsSubmitting(true);
    fetch(server + "/edit_manufacturing_feature", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        manufacturingFeatureId: activeFeature.id,
        feature: payload,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          dispatch(
            showPopupSnackbar({
              error: true,
              message:
                result.message ||
                t(
                  "manufacturing.featureUpdateFailed",
                  "Failed to update feature."
                ),
            })
          );
          return;
        }
        dispatch(
          showPopupSnackbar({
            error: false,
            message: t(
              "manufacturing.featureUpdated",
              "Feature updated successfully."
            ),
          })
        );
        setIsDialogOpen(false);
        setActiveFeature(null);
        fetchFeatureItems();
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            error: true,
            message: t(
              "manufacturing.featureUpdateFailed",
              "Failed to update feature."
            ),
          })
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleDialogSubmit = (values) => {
    if (activeFeature) {
      handleUpdateFeature(values);
    } else {
      handleCreateFeature(values);
    }
  };

  const handleDeleteFeature = () => {
    if (!activeFeature) {
      return;
    }
    setIsDeleting(true);
    fetch(server + "/delete_manufacturing_feature", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        manufacturingFeatureId: activeFeature.id,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          dispatch(
            showPopupSnackbar({
              error: true,
              message:
                result.message ||
                t(
                  "manufacturing.featureDeleteFailed",
                  "Failed to delete feature."
                ),
            })
          );
          return;
        }
        dispatch(
          showPopupSnackbar({
            error: false,
            message: t(
              "manufacturing.featureDeleted",
              "Feature deleted successfully."
            ),
          })
        );
        setIsDialogOpen(false);
        setActiveFeature(null);
        fetchFeatureItems();
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            error: true,
            message: t(
              "manufacturing.featureDeleteFailed",
              "Failed to delete feature."
            ),
          })
        );
      })
      .finally(() => setIsDeleting(false));
  };

  const headCells = useMemo(
    () => [
      {
        id: "name",
        label: t("manufacturing.featureName", "Feature name"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "description",
        label: t("manufacturing.featureDescription", "Description"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "stockItemLabel",
        label: t("manufacturing.linkedStockItem", "Linked stock item"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "listCount",
        label: t("manufacturing.featureLists", "Feature lists"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "listNamesLabel",
        label: t("manufacturing.featureListNames", "List names"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "updatedAt",
        label: t("updatedAt", "Updated"),
        disablePadding: false,
        numeric: false,
      },
    ],
    [t]
  );

  const tableData = useMemo(
    () =>
      filteredFeatures.map((feature) => ({
        ...feature,
        stockItemLabel: feature.StockItem
          ? [feature.StockItem.sku, feature.StockItem.name]
              .filter(Boolean)
              .join(" – ")
          : "",
        listCount: feature.listNames?.length ?? 0,
        listNamesLabel: (feature.listNames || [])
          .map((entry) => entry.name)
          .join(", "),
      })),
    [filteredFeatures]
  );

  const specialFormatting = useMemo(
    () => [
      {
        id: "updatedAt",
        format: (value) => (value ? formatTimeWithTZOffset(value, ECABIN) : ""),
      },
    ],
    []
  );

  return (
    <Grid container spacing={3}>
      <FeatureItemDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        feature={activeFeature}
        isSubmitting={isSubmitting}
        isDeleting={isDeleting}
        onDelete={
          activeFeature
            ? () => {
                handleDeleteFeature();
              }
            : null
        }
      />
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href="/cpanel/manufacturing">
            {t("manufacturing.header", "Manufacturing")}
          </Link>
          <Link color="inherit" href="/cpanel/manufacturing/features">
            {t("manufacturing.featureLists", "Feature lists")}
          </Link>
          <Typography color="textPrimary">
            {t("manufacturing.featureItems", "Feature items")}
          </Typography>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddClick}
              >
                {t("manufacturing.addFeature", "Add feature")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12}>
              <Title>{t("manufacturing.featureItems", "Feature items")}</Title>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                variant="outlined"
                fullWidth
                label={t("common.search", "Search")}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </Grid>
          </Grid>
          {errorMessage ? (
            <Grid container style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  style={{ padding: 16, background: "#ffeeee" }}
                >
                  {errorMessage}
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          {isLoading ? (
            <CircularProgress />
          ) : tableData.length ? (
            <SortableTable
              tableLayout="fixed"
              headCells={headCells}
              tableData={tableData}
              rowsPerPage={25}
              defaultSortRow="name"
              alternateColors
              getRowStyle={tableAlternateColors}
              specialDataFormatting={specialFormatting}
              handleRowClick={(row) => {
                setActiveFeature(row);
                setIsDialogOpen(true);
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              {searchValue
                ? t(
                    "manufacturing.noFeatureItemsMatchSearch",
                    "No feature items match your search."
                  )
                : t(
                    "manufacturing.noFeatureItems",
                    "No feature items available."
                  )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
